/**
 * Structured logging utility using react-native-logs
 *
 * Provides consistent logging across iOS, Android, and Web platforms.
 * Integrates with OpenTelemetry for trace correlation.
 *
 * Usage:
 *   import {logger, serializeError} from 'app/utils/logger';
 *
 *   logger.debug('UserStore', 'Loading user data', {userId: 123});
 *   logger.info('CartStore', 'Item added to cart', {productId: 456});
 *   logger.warn('PaymentFlow', 'Payment retry required');
 *   logger.error('APIService', 'Request failed', serializeError(err));
 */

import {Platform} from 'react-native';
import {consoleTransport, logger as rnLogger} from 'react-native-logs';

import rnuc from '../config.generated';

import {setLoggerCallback, telemetryConfig} from './telemetry/config';
import {tracer} from './telemetry/tracer';
import dev from './dev';

/**
 * Log levels with numeric values (lower = more verbose)
 */
export enum LogLevel {
  TRACE = 0,
  DEBUG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
}

/**
 * Logger configuration
 */
interface LoggerConfig {
  /**
   * Minimum log level to output
   * - Development: DEBUG (shows debug, info, warn, error)
   * - Production: INFO (shows info, warn, error)
   */
  severity: keyof typeof LogLevel;

  /**
   * Enable/disable logging entirely
   * Controlled by OTEL_ENABLED or dev mode
   */
  enabled: boolean;

  /**
   * Enable debug mode (more verbose output)
   * Controlled by OTEL_DEBUG env var
   */
  debug: boolean;
}

/**
 * Check if logging is explicitly disabled via environment variable
 * Set LOGGING_ENABLED: false in .env.[brand].yml to disable
 */
function isLoggingDisabled(): boolean {
  const value = rnuc.LOGGING_ENABLED;
  // Check for explicit disable - handle boolean, string, and number variants
  return (
    value === false ||
    value === 'false' ||
    value === 0 ||
    value === '0' ||
    (typeof value === 'string' && value.toLowerCase() === 'false')
  );
}

/**
 * Get logger configuration from environment
 */
function getLoggerConfig(): LoggerConfig {
  // Check for explicit disable first
  if (isLoggingDisabled()) {
    return {
      severity: 'INFO',
      enabled: false,
      debug: false,
    };
  }

  // Default to production settings
  let isDebugMode = false;

  // Try to determine if we're in development mode
  try {
    isDebugMode = dev.enableDebugging();
  } catch (_error) {
    // In test environment or other contexts where dev.enableDebugging() fails,
    // default to INFO level with telemetry config
  }

  // In development, always enable logging with DEBUG level
  if (isDebugMode) {
    return {
      severity: 'DEBUG',
      enabled: true,
      debug: telemetryConfig?.debug || false,
    };
  }

  // In production, use environment configuration
  return {
    severity: 'INFO',
    enabled: telemetryConfig?.enabled || false,
    debug: telemetryConfig?.debug || false,
  };
}

// Lazy initialization - logger is created on first access
let _log: ReturnType<typeof rnLogger.createLogger> | null = null;
let _loggerConfig: LoggerConfig | null = null;

/**
 * Get or create the logger instance (lazy initialization)
 */
function getLogger(): ReturnType<typeof rnLogger.createLogger> {
  if (!_log) {
    _loggerConfig = getLoggerConfig();

    /**
     * Create configured logger instance
     *
     * Note: Colors are disabled on web platform because browser consoles
     * don't support ANSI escape codes properly (they render literally).
     * Colors work fine on iOS and Android.
     *
     * Async logging is disabled in test environments to prevent "Cannot log after tests are done" warnings.
     */
    _log = rnLogger.createLogger({
      levels: {
        TRACE: LogLevel.TRACE,
        DEBUG: LogLevel.DEBUG,
        INFO: LogLevel.INFO,
        WARN: LogLevel.WARN,
        ERROR: LogLevel.ERROR,
      },
      severity: _loggerConfig.severity,
      transport: consoleTransport,
      transportOptions: {
        colors:
          Platform.OS === 'web'
            ? undefined
            : {
                TRACE: 'grey',
                DEBUG: 'cyan',
                INFO: 'blueBright',
                WARN: 'yellowBright',
                ERROR: 'redBright',
              },
      },
      async: process.env.NODE_ENV !== 'test', // Disable async in tests to prevent timing issues
      dateFormat: 'time',
      printLevel: true,
      printDate: true,
      enabled: _loggerConfig.enabled,
    });
  }
  return _log!; // Non-null assertion since we just created it if it was null
}

/**
 * Get the logger config (lazy initialization)
 */
function getConfig(): LoggerConfig {
  if (!_loggerConfig) {
    _loggerConfig = getLoggerConfig();
  }
  return _loggerConfig;
}

/**
 * Get current trace context for log correlation
 * Uses snake_case to align with backend conventions
 */
function getTraceContext(): {trace_id?: string; span_id?: string} {
  // Guard against telemetry config not being available (e.g., in tests)
  if (!telemetryConfig || !telemetryConfig.enabled) {
    return {};
  }

  try {
    const trace = tracer.getCurrentTrace();
    if (!trace) {
      return {};
    }

    return {
      trace_id: trace.traceId,
      span_id: trace.spanId,
    };
  } catch (_error) {
    // If tracer fails (e.g., in tests), return empty context
    return {};
  }
}

/**
 * Format log message with namespace and trace context
 */
function formatLogMessage(
  namespace: string,
  _message: string,
  data?: Record<string, unknown>,
): [string, Record<string, unknown>] {
  const prefix = `[${namespace}]`;
  const traceContext = getTraceContext();

  const metadata = {
    ...traceContext,
    ...data,
  };

  return [prefix, metadata];
}

/**
 * Application logger with namespace support and OTEL integration
 */
export const logger = {
  /**
   * Trace-level logging (most verbose)
   * Use for fine-grained debugging information
   */
  trace(namespace: string, message: string, data?: Record<string, unknown>): void {
    const [prefix, metadata] = formatLogMessage(namespace, message, data);
    const log = getLogger();
    log.TRACE(`${prefix} ${message}`, metadata);
  },

  /**
   * Debug-level logging
   * Use for detailed information useful during development
   */
  debug(namespace: string, message: string, data?: Record<string, unknown>): void {
    const [prefix, metadata] = formatLogMessage(namespace, message, data);
    const log = getLogger();
    log.DEBUG(`${prefix} ${message}`, metadata);
  },

  /**
   * Info-level logging
   * Use for general informational messages
   */
  info(namespace: string, message: string, data?: Record<string, unknown>): void {
    const [prefix, metadata] = formatLogMessage(namespace, message, data);
    const log = getLogger();
    log.INFO(`${prefix} ${message}`, metadata);
  },

  /**
   * Warning-level logging
   * Use for potentially harmful situations that don't prevent execution
   */
  warn(namespace: string, message: string, data?: Record<string, unknown>): void {
    const [prefix, metadata] = formatLogMessage(namespace, message, data);
    const log = getLogger();
    log.WARN(`${prefix} ${message}`, metadata);
  },

  /**
   * Error-level logging
   * Use for error events that might still allow the app to continue
   */
  error(namespace: string, message: string, data?: Record<string, unknown>): void {
    const [prefix, metadata] = formatLogMessage(namespace, message, data);
    const log = getLogger();
    log.ERROR(`${prefix} ${message}`, metadata);
  },

  /**
   * Get current log level severity
   */
  getSeverity(): string {
    return getConfig().severity;
  },

  /**
   * Check if logger is enabled
   */
  isEnabled(): boolean {
    return getConfig().enabled;
  },

  /**
   * Check if debug mode is enabled
   */
  isDebugEnabled(): boolean {
    return getConfig().debug;
  },
};

/**
 * Serializes an error object into a JSON-friendly format for logging.
 *
 * JavaScript Error objects don't serialize properly to JSON (they produce {}).
 * This function extracts the useful properties from errors, including
 * Axios-specific response data when available.
 *
 * @param err - The error to serialize (can be Error, AxiosError, or unknown)
 * @returns A plain object with error details that serializes properly to JSON
 *
 * @example
 * // Instead of: logger.error('API', 'Failed', {error: err})  // logs {}
 * // Use:        logger.error('API', 'Failed', serializeError(err))
 *
 * @example
 * // With additional context:
 * logger.error('API', 'Failed', {...serializeError(err), userId: 123})
 */
export function serializeError(err: unknown): Record<string, unknown> {
  if (!err) {
    return {error: null};
  }

  // Handle non-Error types
  if (!(err instanceof Error)) {
    return {
      error: String(err),
      errorType: typeof err,
    };
  }

  // Base error properties
  const serialized: Record<string, unknown> = {
    errorMessage: err.message,
    errorName: err.name,
  };

  // Include stack trace in development only (can be verbose)
  if (__DEV__) {
    serialized.errorStack = err.stack;
  }

  // Handle Axios errors (have response property)
  const axiosError = err as {
    response?: {
      status?: number;
      statusText?: string;
      data?: unknown;
    };
    config?: {
      url?: string;
      method?: string;
    };
    code?: string;
  };

  if (axiosError.response) {
    serialized.responseStatus = axiosError.response.status;
    serialized.responseStatusText = axiosError.response.statusText;
    serialized.responseData = axiosError.response.data;
  }

  if (axiosError.config) {
    serialized.requestUrl = axiosError.config.url;
    serialized.requestMethod = axiosError.config.method;
  }

  if (axiosError.code) {
    serialized.errorCode = axiosError.code;
  }

  return serialized;
}

/**
 * Export logger config getter for testing
 */
export const loggerConfig = {
  get severity() {
    return getConfig().severity;
  },
  get enabled() {
    return getConfig().enabled;
  },
  get debug() {
    return getConfig().debug;
  },
};

/**
 * Export default logger instance
 */
export default logger;

/**
 * Register logger callback for telemetry to break circular dependency
 * This allows telemetry modules to log without importing logger directly
 */
setLoggerCallback((namespace: string, message: string, metadata) => {
  const {level, context} = metadata;

  switch (level) {
    case 'trace':
      logger.trace(namespace, message, context);
      break;
    case 'debug':
      logger.debug(namespace, message, context);
      break;
    case 'info':
      logger.info(namespace, message, context);
      break;
    case 'warn':
      logger.warn(namespace, message, context);
      break;
    case 'error':
      logger.error(namespace, message, context);
      break;
  }
});
